import React, { useState, useEffect, useContext } from 'react';
import {
  User,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  Camera,
  Shield
} from 'lucide-react';
import '../../assets/css/ngodashboard/NgoSettings.css';
import NGOMasterLayout from '../../components/layout/NGOMasterLayout';
import AppContext from '../../context/AppContext';
import axios from 'axios';
import Swal from 'sweetalert2';

const NGOSettings = () => {
  const { baseUrl, loggedInNgoId } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState('profile');

  // ✅ Profile State
  const [fullName, setFullName]     = useState('');
  const [email, setEmail]           = useState('');
  const [phone, setPhone]           = useState('');
  const [profileImage, setProfileImage] = useState('');

  // ✅ Security State
  const [oldPassword, setOldPassword]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ✅ Load from localStorage
  useEffect(() => {
    const ngoData = localStorage.getItem('loggedInNgo');
    if (ngoData) {
      const parsed = JSON.parse(ngoData);
      setFullName(parsed.fullname       || '');
      setEmail(parsed.email             || '');
      setPhone(parsed.phone             || '');
      setProfileImage(parsed.profile_image || '');
    }
  }, []);

  // ✅ Profile Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setProfileImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ✅ Update Profile
  const handleSaveProfile = () => {
    const formData = new FormData();
    formData.append('ngo_id',   loggedInNgoId);
    formData.append('fullname', fullName);
    formData.append('email',    email);
    formData.append('phone',    phone);

    axios.post(`${baseUrl}/ngo/${loggedInNgoId}/updateProfile`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ title: 'Saved!', text: 'Profile has been updated.', icon: 'success' });
          localStorage.setItem('loggedInNgo', JSON.stringify(response.data.ngo));
        } else {
          Swal.fire({ title: 'Error!', text: 'Failed to update profile.', icon: 'error' });
        }
      })
      .catch((error) => {
        Swal.fire({ title: 'Oops!', text: error.message, icon: 'error' });
      });
  };

  // ✅ Update Password
  const handleUpdatePassword = () => {
    if (newPassword.trim() !== confirmPassword.trim()) {
      Swal.fire({ title: 'Error!', text: 'New password and confirm password do not match.', icon: 'error' });
      return;
    }

    const formData = new FormData();
    formData.append('ngo_id',      loggedInNgoId);
    formData.append('old_password', oldPassword);
    formData.append('password',     newPassword);

    axios.post(`${baseUrl}/ngo/${loggedInNgoId}/updatePassword`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ title: 'Saved!', text: 'Password has been updated.', icon: 'success' });
          localStorage.setItem('loggedInNgo', JSON.stringify(response.data.ngo));
          setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        } else {
          Swal.fire({ title: 'Error!', text: 'Failed to update password.', icon: 'error' });
        }
      })
      .catch((error) => {
        Swal.fire({ title: 'Oops!', text: error.message, icon: 'error' });
      });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <NGOMasterLayout>
      <div className="settings-page-container">

        {/* Page Header */}
        <div className="settings-header-banner">
          <div className="header-text-wrapper">
            <h1 className="settings-main-heading">Settings</h1>
            <p className="settings-sub-heading">Manage your account preferences and security</p>
          </div>
          <div className="header-icon-section">
            <Shield size={56} className="settings-shield-icon" />
          </div>
        </div>

        {/* Tabs Card */}
        <div className="settings-tabs-card">

          {/* ✅ Sirf 2 tabs */}
          <div className="settings-nav-tabs">
            <button
              className={`settings-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={18} /> Profile
            </button>
            <button
              className={`settings-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Lock size={18} /> Security
            </button>
          </div>

          <div className="tab-content-area">

            {/* ===== PROFILE TAB ===== */}
            {activeTab === 'profile' && (
              <div className="settings-tab-content">
                <div className="settings-row">

                  {/* Left - Profile Image */}
                  <div className="settings-col-left">
                    <div className="profile-image-section">
                      <div className="profile-picture-wrapper">
                        <img
                          src={profileImage || `https://ui-avatars.com/api/?name=${fullName}&size=200&background=3b82f6&color=fff`}
                          alt="Profile"
                          className="profile-display-image"
                        />
                        <label htmlFor="profileImageInput" className="image-upload-overlay">
                          <Camera size={24} />
                          <input
                            type="file"
                            id="profileImageInput"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      <h5 className="profile-user-name">{fullName}</h5>
                      <p className="profile-user-role">NGO Account</p>
                    </div>
                  </div>

                  {/* Right - Profile Form */}
                  <div className="settings-col-right">
                    <div className="profile-details-form">
                      <div className="form-inner">

                        <div className="form-group-custom">
                          <label className="form-input-label"><User size={16} /> Full Name</label>
                          <input
                            type="text"
                            className="custom-form-input"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                          />
                        </div>

                        <div className="form-group-custom">
                          <label className="form-input-label"><Mail size={16} /> Email Address</label>
                          <input
                            type="email"
                            className="custom-form-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          <span className="verified-email-text"><CheckCircle size={14} /> Verified</span>
                        </div>

                        <div className="form-group-custom">
                          <label className="form-input-label"><Phone size={16} /> Phone Number</label>
                          <input
                            type="tel"
                            className="custom-form-input"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </div>

                        <div className="form-action-buttons">
                          <button className="save-changes-btn" onClick={handleSaveProfile}>Update Profile</button>
                          <button className="cancel-action-btn" onClick={() => setActiveTab('profile')}>Cancel</button>
                        </div>

                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* ===== SECURITY TAB ===== */}
            {activeTab === 'security' && (
              <div className="settings-tab-content">
                <div className="settings-row">
                  <div className="settings-col-half">
                    <div className="password-management-card">
                      <div className="form-inner">
                        <h5 className="section-card-title"><Lock size={20} /> Change Password</h5>

                        <div className="form-group-custom">
                          <label className="form-input-label">Current Password</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showPassword.current ? 'text' : 'password'}
                              className="custom-form-input"
                              value={oldPassword}
                              onChange={(e) => setOldPassword(e.target.value)}
                            />
                            <button type="button" className="password-toggle-icon" onClick={() => togglePasswordVisibility('current')}>
                              {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <div className="form-group-custom">
                          <label className="form-input-label">New Password</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showPassword.new ? 'text' : 'password'}
                              className="custom-form-input"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <button type="button" className="password-toggle-icon" onClick={() => togglePasswordVisibility('new')}>
                              {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <div className="form-group-custom">
                          <label className="form-input-label">Confirm New Password</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showPassword.confirm ? 'text' : 'password'}
                              className="custom-form-input"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <button type="button" className="password-toggle-icon" onClick={() => togglePasswordVisibility('confirm')}>
                              {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <div className="form-action-buttons">
                          <button className="save-changes-btn" onClick={handleUpdatePassword}>Reset Password</button>
                          <button className="cancel-action-btn" onClick={() => { setOldPassword(''); setNewPassword(''); setConfirmPassword(''); }}>Cancel</button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </NGOMasterLayout>
  );
};

export default NGOSettings;